import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { User as UiApiUser, UserGoogleDriveCredential } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { setUserStore, userStore } from '@/services/auth/store';
import type { OAuthOpenResponse } from '@/services/oauth';
import type { AugmentedAccount, AugmentedUser, Workspace } from '@/services/auth/types';
import { deleteCrossAppCacheByPrefix } from '@/utils/cross-app-cache';
import type { HookMutationConfig } from '../shared/react-query-types';
import { googleDriveCredentialKeys } from './queries';

/**
 * The part of the updated user these mutations report back. The endpoints return
 * the whole user, but only the identity and the credential itself are consumed,
 * and the absent credential is normalised to `null` for the connected/disconnected
 * checks callers make.
 */
export type GoogleDriveCredentialUser = {
  id: string;
  google_drive_credential: UserGoogleDriveCredential | null;
};

export type CreateGoogleDriveCredentialInput = {
  accountId: number;
  userId: number;
  oauth: Pick<OAuthOpenResponse, 'authorizationCode' | 'redirectUri'>;
};

function pathParams({ accountId, userId }: { accountId: number; userId: number }) {
  return { account_id: String(accountId), id: String(userId) };
}

function toCredentialUser(user: UiApiUser | undefined): GoogleDriveCredentialUser {
  if (user == null) {
    throw new Error('Google Drive credential response did not include the updated user');
  }

  return { id: user.id, google_drive_credential: user.google_drive_credential ?? null };
}

export async function createGoogleDriveCredential(
  input: CreateGoogleDriveCredentialInput,
): Promise<GoogleDriveCredentialUser> {
  const response = await hydraUiApi.users.createGoogleDriveCredential({
    params: { path: pathParams(input) },
    // Unlike the rest of ui_api, this endpoint binds camelCase request keys.
    body: {
      credential: {
        authorizationCode: input.oauth.authorizationCode,
        redirectUri: input.oauth.redirectUri,
      },
    },
  });

  return toCredentialUser(response.user);
}

type AuthenticatedQueryData = {
  account: AugmentedAccount | null;
  user: AugmentedUser | null;
  workspaces: Workspace[] | null;
};

function syncGoogleDriveCredentialToAuthStore(user: GoogleDriveCredentialUser): void {
  const currentUser = userStore.get();

  if (currentUser == null || String(currentUser.id) !== user.id) {
    return;
  }

  setUserStore({
    ...currentUser,
    google_drive_credential: user.google_drive_credential,
  });
}

function syncGoogleDriveCredentialToAuthenticatedQuery(
  queryClient: QueryClient,
  user: GoogleDriveCredentialUser,
): void {
  queryClient.setQueryData<AuthenticatedQueryData>(['authenticated'], (currentAuth) => {
    if (currentAuth?.user == null || String(currentAuth.user.id) !== user.id) {
      return currentAuth;
    }

    return {
      ...currentAuth,
      user: {
        ...currentAuth.user,
        google_drive_credential: user.google_drive_credential,
      },
    };
  });
}

export function syncGoogleDriveCredentialMutationSuccess({
  accountId,
  queryClient,
  user,
  userId,
}: {
  accountId: number;
  queryClient: QueryClient;
  user: GoogleDriveCredentialUser;
  userId: number;
}): void {
  syncGoogleDriveCredentialToAuthStore(user);
  syncGoogleDriveCredentialToAuthenticatedQuery(queryClient, user);
  deleteCrossAppCacheByPrefix('/account');
  void queryClient.invalidateQueries({ queryKey: ['authenticated'], refetchType: 'none' });
  void queryClient.resetQueries({
    exact: true,
    queryKey: googleDriveCredentialKeys.accessToken(accountId, userId),
  });
}

type UseCreateGoogleDriveCredentialVariables = Pick<CreateGoogleDriveCredentialInput, 'oauth'>;

type UseCreateGoogleDriveCredentialOptions = {
  accountId: number;
  userId: number;
  mutationConfig?: HookMutationConfig<GoogleDriveCredentialUser, UseCreateGoogleDriveCredentialVariables>;
};

export function useCreateGoogleDriveCredential({
  accountId,
  userId,
  mutationConfig,
}: UseCreateGoogleDriveCredentialOptions) {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    ...rest,
    mutationFn: (variables) => createGoogleDriveCredential({ accountId, userId, ...variables }),
    onSuccess: (user, variables, ...callbackArgs) => {
      syncGoogleDriveCredentialMutationSuccess({ accountId, queryClient, user, userId });
      onSuccess?.(user, variables, ...callbackArgs);
    },
  });
}

export type DeleteGoogleDriveCredentialInput = {
  accountId: number;
  userId: number;
};

export async function deleteGoogleDriveCredential(
  input: DeleteGoogleDriveCredentialInput,
): Promise<GoogleDriveCredentialUser> {
  const response = await hydraUiApi.users.deleteGoogleDriveCredential({
    params: { path: pathParams(input) },
  });

  return toCredentialUser(response.user);
}

type UseDeleteGoogleDriveCredentialOptions = DeleteGoogleDriveCredentialInput & {
  mutationConfig?: HookMutationConfig<GoogleDriveCredentialUser, void>;
};

export function useDeleteGoogleDriveCredential({
  accountId,
  userId,
  mutationConfig,
}: UseDeleteGoogleDriveCredentialOptions) {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    ...rest,
    mutationFn: () => deleteGoogleDriveCredential({ accountId, userId }),
    onSuccess: (user, variables, ...callbackArgs) => {
      syncGoogleDriveCredentialMutationSuccess({ accountId, queryClient, user, userId });
      onSuccess?.(user, variables, ...callbackArgs);
    },
  });
}
